import { z } from "zod";
import { useFunnel } from "@airhelp/funnel";
import { FunnelStepContainer } from "../../../components/funnel/ui-elements/FunnelStepContainer";
import { FunnelStepSection } from "../../../components/funnel/ui-elements/FunnelStepSection";
import { useTranslation } from "react-i18next";
import {
  FunnelStepBackControl,
  FunnelStepControls,
  FunnelStepForwardControl,
} from "../../../components/funnel/ui-elements/FunnelStepControls";
import { ControlledRadioGroup, useForm, useFormHandlers } from "../../../modules/form";
import { updateClaimDetails, usePreEligibilityDataSnapshot } from "../state/preEligibilityState";
import { SUPPORTED_CLAIM_TYPES } from "../../../settings/constants";
import { OverbookBlockModal } from "v2/components/funnel/modals/OverbookBlockModal";
import { useState } from "react";
import { useCreateClaimEnquiry } from "../../../modules/hooks/useCreateClaimEnquiry";
import ErrorNotification from "components/notification/error-notification/ErrorNotification";
import { trackClaimProcess } from "../../../modules/analytics/track";
import { useTrackEnquiryStepComplete } from "../../../modules/analytics/hooks/useTrackEnquiryStepComplete";

const defaultStepSchema = z.object({
  claimType: z.enum(SUPPORTED_CLAIM_TYPES).nullable(),
});

const stepSchema = z.object({
  claimType: z.enum(SUPPORTED_CLAIM_TYPES),
});

export function ClaimTypeStep() {
  const funnel = useFunnel();
  const { t } = useTranslation();
  const { claimDetails, itineraryFlights, flightDetails } = usePreEligibilityDataSnapshot();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { createClaimEnquiry } = useCreateClaimEnquiry();
  const trackEnquiryStepComplete = useTrackEnquiryStepComplete({
    currentStep: funnel.currentStep?.name,
  });

  const options = flightDetails.isFutureFlight
    ? SUPPORTED_CLAIM_TYPES.filter((type) => type !== "delayed")
    : SUPPORTED_CLAIM_TYPES;

  const {
    control,
    watch,
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = useForm({
    schema: stepSchema,
    defaultValues: {
      claimType: claimDetails.claimType,
    },
  });

  const isOverbooked = watch("claimType") === "overbooked";

  const { onSubmit, showError } = useFormHandlers({
    schema: defaultStepSchema,
    async onSubmit({ claimType }) {
      const updatedClaimDetails = updateClaimDetails({ claimType });

      if (funnel.currentStep?.meta?.claimShouldBeSubmitted) {
        const rawClaimEnquiry = await createClaimEnquiry({
          itineraryFlights,
          claimDetails: updatedClaimDetails,
        });
        trackClaimProcess({
          funnelStep: funnel.currentStep?.name,
          isEligible: rawClaimEnquiry?.claim_valid,
        });
        trackEnquiryStepComplete();
      } else {
        trackClaimProcess({ funnelStep: funnel.currentStep?.name });
        trackEnquiryStepComplete();
        funnel.stepForward();
      }
    },
  });
  return (
    <FunnelStepContainer data-testid={"ClaimTypeStep"}>
      <FunnelStepSection title={t("steps.claim_type.text")}>
        <ControlledRadioGroup
          name={"claimType"}
          control={control}
          options={options}
          renderOption={(option) => t(`steps.claim_type.${option}`)}
        />
      </FunnelStepSection>

      {showError && <ErrorNotification message={t("components.funnel_step.try_again")} />}

      <FunnelStepControls>
        <FunnelStepBackControl />
        <FunnelStepForwardControl
          isDisabled={!isValid}
          isLoading={isSubmitting}
          onClick={isOverbooked ? () => setIsModalOpen(true) : handleSubmit(onSubmit)}
        />
      </FunnelStepControls>
      <OverbookBlockModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleSubmit(onSubmit)}
      />
    </FunnelStepContainer>
  );
}
